import React from 'react';
import DocumentTitle from 'react-document-title';
import { errorMessage, customMessage } from 'actions/internal';
import TopNavBar from 'containers/nav/TopNavBar';
import SubNavBar from 'containers/nav/SubNavBar';
import AdministrationSubNav from 'components/administration/AdministrationSubNav';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { appNamedRoutes } from 'routes';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import axios from 'axios';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from 'actions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import AccountInputs from './AccountInputs';
import { useForm, FormProvider } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from 'components/helpers/LoadingSpinner';

const AdministrationFamiliesEditPage = ({
  auth,
  match,
  history,
  onErrorMessage,
  onCustomMessage,
  onShowLoading,
  onHideLoading,
}) => {
  const queryClient = useQueryClient();
  const { id: accountId } = match.params;
  const queryKey = ['administration', 'accounts', accountId];
  const formMethods = useForm({ mode: 'onChange' })

  const accountQuery = useQuery({
    queryKey,
    queryFn: ({ signal }) => axios.get(
      Routes.api_dashboard_administration_account_path({
        id: accountId,
      }),
      { headers: authHeaders(), signal }
    ).then(response => {
      const account = {};
      account.id = response.data.id;
      account.partner_id = response.data.partner_id;
      account.first_name = response.data.first_name;
      account.last_name = response.data.last_name;
      account.email = response.data.email;
      account.address = response.data.address;
      account.zip = response.data.zip;
      account.phone = response.data.phone;
      account.players_attributes = [];

      response.data.players.forEach((player) => {
        account.players_attributes.push({
          id: player.id,
          first_name: player.first_name,
          last_name: player.last_name,
          gender: player.gender,
          race: player.race,
          birthday: player.birthday,
          weeks_premature: player.weeks_premature,
          cohort_ids: player.cohorts.map(cohort => cohort.id),
          cohorts: player.cohorts,
          sponsors_accepeted: player.cohorts.length > 0,
          custom_ids: player.custom_ids,
        });
      });

      return account;
    }),
  })

  const saveAccount = useMutation({
    mutationKey: ['administration', 'accounts', 'save'],
    mutationFn: (account) => {
      return axios.put(Routes.api_dashboard_administration_account_path({
        id: accountId,
      }), { account: account }, { headers: authHeaders() })
    },
    onSuccess: () => {
      onCustomMessage('Account saved successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['administraiton', 'accounts'] });
    },
    onError: (error) => {
      if (error.response.status === 500) {
        onErrorMessage('Oops! Something went wrong. Contact support for help.');
        return
      }

      if (error.response.status === 422) {
        onErrorMessage('Please check the form for errors and try again.');
        const account = error.response.data;
        const players = account.players || [];

        Object.entries(account.errors).forEach(([field, errors]) => {
          if (!field.includes('.')) {
            formMethods.setError(field, { type: 'manual', message: errors.join(', ') })
          }
        });

        players.forEach((player, index) => {
          Object.entries(player.errors).forEach(([field, errors]) => {
            formMethods.setError(`players_attributes[${index}].${field}`, { type: 'manual', message: errors.join(', ') })
          });
        })

        return
      }
    },
  })

  const resetPassword = useMutation({
    mutationFn: (accountId) => {
      return axios.put(Routes.reset_password_api_dashboard_administration_account_path(
        accountId
      ), {}, { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
      onCustomMessage('Password instructions sent successfully', 'success');
    },
    onError: (error) => {
      if (error.response.status === 500) {
        onErrorMessage('Oops! Something went wrong. Contact support for help.');
        return
      }

      onErrorMessage(error.response.data.message);
    },
  })

  const requestDeleteAccount = useMutation({
    mutationFn: (accountId) => {
      return axios.put(Routes.request_delete_account_api_dashboard_administration_account_path(
        accountId
      ), {}, { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
      onCustomMessage('Request sent successfully', 'success');
    },
    onError: (error) => {
      if (error.response.status === 500) {
        onErrorMessage('Oops! Something went wrong. Contact support for help.');
        return
      }

      onErrorMessage(error.response.data.message);
    },
  })

  const requestDeletePlayer = useMutation({
    mutationFn: ({accountId, playerId}) => {
      return axios.put(Routes.request_delete_player_api_dashboard_administration_account_path(
        accountId, playerId
      ), {}, { headers: authHeaders() })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey)
      onCustomMessage('Request sent successfully', 'success');
    },
    onError: (error) => {
      if (error.response.status === 500) {
        onErrorMessage('Oops! Something went wrong. Contact support for help.');
        return
      }

      onErrorMessage(error.response.data.message);
    },
  })

  const onSubmit = async (data) => {
    saveAccount.mutate(data);
  };

  React.useEffect(() => {
    if (accountQuery.isError) {
      onErrorMessage('Oops! Something went wrong. Contact support for help.');
    }

    if (accountQuery.isPending) {
      onShowLoading();
    } else {
      onHideLoading();
      formMethods.reset(accountQuery.data);
    }
  }, [
    accountQuery.isPending,
    accountQuery.isError,
    accountQuery.data,
    onHideLoading,
    onShowLoading,
    onErrorMessage,
    formMethods,
  ]);

  if (!accountQuery.data) {
    return (<DocumentTitle title='Sparkler - Administration'>
      <div>
        <TopNavBar />
        <SubNavBar />
        <AdministrationSubNav />
        <div className="row content">
          <div className="column">
            <LoadingSpinner bgColor='light' />
          </div>
        </div>
      </div>
    </DocumentTitle>
    );
  }

  return (<DocumentTitle title='Sparkler - Administration'>
    <div>
      <TopNavBar />
      <SubNavBar />
      <AdministrationSubNav />
      <div className="row">
        <div className="column">
          <div className='u-margin-top--base u-margin-bottom--large'>
            <span className='o-sp-button o-sp-button--back' onClick={() => {
              const queryParams = history.location.search;
              history.push(`${appNamedRoutes.administrationFamilies}${queryParams}`)
            }}>
              <i className='sp-icon-arrow-left'></i>&nbsp;Back
            </span>
          </div>
          <div className="row u-margin-top--base">
            <div className="column">
              <FormProvider {...formMethods}>
                <form onSubmit={(event) => {
                  formMethods.clearErrors();
                  formMethods.handleSubmit(onSubmit)(event);
                }}>
                  <AccountInputs
                    auth={auth}
                    onRequestToDeleteAccount={(accountId) => { window.confirm('Are you sure you want to request to delete this account?') && requestDeleteAccount.mutate(accountId) }}
                    onRequestToDeletePlayer={(accountId, playerId) => { window.confirm('Are you sure you want to request to delete this child?') && requestDeletePlayer.mutate({accountId, playerId}) }}
                    onResetPassword={(accountId) => { window.confirm('Are you sure you want send password reset instructions?') && resetPassword.mutate(accountId) }}
                  />
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DocumentTitle>
  );
}

AdministrationFamiliesEditPage.propTypes = {
  auth: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onHideLoading: PropTypes.func.isRequired,
  onErrorMessage: PropTypes.func.isRequired,
  onCustomMessage: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    onErrorMessage: (message) => {
      dispatch(errorMessage(message))
    },
    onCustomMessage: (message, type) => {
      dispatch(customMessage(message, type))
    },
    onShowLoading: () => {
      dispatch(showLoading())
    },
    onHideLoading: () => {
      dispatch(hideLoading())
    },
  }
};


export default connect(mapStateToProps, mapDispatchToProps)(AdministrationFamiliesEditPage);
